import React, { useState, useEffect, useMemo, useRef } from 'react';
import './CurrencyDropdown.scss';

// Currency options for invoices
const CURRENCY_OPTIONS = [
  { key: 'CLP', value: 'CLP', text: 'CLP', country: 'Chile' },
  { key: 'USD', value: 'USD', text: 'USD', country: 'Estados Unidos' },
  { key: 'EUR', value: 'EUR', text: 'EUR', country: 'Europa' },
  { key: 'MXN', value: 'MXN', text: 'MEX', country: 'México' }, // MXN is the standard code for Mexican Peso
  { key: 'ARS', value: 'ARS', text: 'ARG', country: 'Argentina' }, // ARS is the standard code for Argentine Peso
  { key: 'CNY', value: 'CNY', text: 'CNY', country: 'China' },
  { key: 'JPY', value: 'JPY', text: 'YEN', country: 'Japón' }, // JPY is the standard code for Japanese Yen
  { key: 'PEN', value: 'PEN', text: 'PEN', country: 'Perú' }, // PEN is the standard code for Peruvian Sol
  { key: 'BRL', value: 'BRL', text: 'BRL', country: 'Brazil' }, // BRL is the standard code for Brazilian Real
  { key: 'COP', value: 'COP', text: 'COP', country: 'Colombia' }, // COP is the standard code for Colombian Peso
  { key: 'CAD', value: 'CAD', text: 'CAD', country: 'Canadá' }, // CAD is the standard code for Canadian Dollar
  { key: 'GBP', value: 'GBP', text: 'GBP', country: 'Reino Unido' }, // GBP is the standard code for British Pound
];

// Currency options for non-invoice obligations (CLP, UF, UTM, USD, EUR)
const OBLIGATION_CURRENCY_OPTIONS = [
  { key: 'CLP', value: 'CLP', text: 'CLP', country: 'Chile' },
  { key: 'UF', value: 'UF', text: 'UF', country: 'Chile' },
  { key: 'UTM', value: 'UTM', text: 'UTM', country: 'Chile' },
  { key: 'USD', value: 'USD', text: 'USD', country: 'Estados Unidos' },
  { key: 'EUR', value: 'EUR', text: 'EUR', country: 'Europa' },
];

const CurrencyDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Seleccione moneda",
  className = "",
  disabled = false,
  id,
  obligationMode = false, // New prop to use obligation currency options
  ...props 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [defaultValue, setDefaultValue] = useState(value);
  const [selectedText, setSelectedText] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Use obligation currency options if obligationMode is true
  const options = obligationMode ? OBLIGATION_CURRENCY_OPTIONS : CURRENCY_OPTIONS;

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.text.toLowerCase().includes(query) || 
      option.value.toLowerCase().includes(query) ||
      option.country.toLowerCase().includes(query)
    );
  }, [searchQuery, options]);

  // Update defaultValue and selectedText when value prop changes
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      setDefaultValue(value);
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption) {
        setSelectedText(`${selectedOption.country} (${selectedOption.text})`);
      } else {
        setSelectedText('');
      }
    } else {
      setDefaultValue(null);
      setSelectedText('');
    }
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle option selection
  const handleSelect = (option) => {
    setDefaultValue(option.value);
    setSelectedText(`${option.country} (${option.text})`);
    setIsOpen(false);
    setSearchQuery('');
    
    if (onChange) {
      onChange(option.value);
    }
  };

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelect(filteredOptions[0]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  // Handle clearing selection
  const handleClear = () => {
    setDefaultValue(null);
    setSelectedText('');
    setIsOpen(false);
    setSearchQuery('');
    
    if (onChange) {
      onChange('');
    }
  };

  const displayValue = isOpen ? searchQuery : selectedText;
  const showPlaceholder = !selectedText && !isOpen;

  return (
    <div 
      ref={dropdownRef}
      className={`currency-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
      {...props}
    >
      <div className="dropdown-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        />
        {defaultValue && !disabled && (
          <span 
            className="dropdown-clear"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={{
              position: 'absolute',
              right: '36px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              zIndex: 10
            }}
            title="Limpiar selección"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" width="16" height="16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l8 8M14 6l-8 8"/>
            </svg>
          </span>
        )}
        <span className="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
      </div>
      {isOpen && !disabled && (
        <div className="dropdown-menu">
          {filteredOptions.length > 0 ? (
            <ul className="dropdown-options" role="listbox">
              {filteredOptions.map((option) => (
                <li
                  key={option.key}
                  className={`dropdown-option ${defaultValue === option.value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={defaultValue === option.value}
                >
                  <span className="option-text">{option.country} ({option.text})</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="dropdown-no-results">
              {searchQuery ? "No se encontraron resultados." : "No hay monedas disponibles."}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CurrencyDropdown;

